import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef } from '@angular/core';
import { WizardStateService } from '../../../services/wizard-state.service';
import { CurrentUserService } from '../../../../../services/current-user.service';
import { AiPortraitTokenService } from '../../../../../pages/player/modals/ai-generation/service/ai-portrait-token.service';
import { MapCreationService } from '../../../services/map-creation.service';
import { AiGenerationMode } from '../../../constants/ai-modes.constants';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ToastService } from '../../../../../services/toast.service';

@Component({
  selector: 'app-review-create-step',
  templateUrl: './review-create-step.component.html',
  styleUrls: ['./review-create-step.component.scss'],
  standalone: false
})
export class ReviewCreateStepComponent implements OnInit, OnDestroy {
  @Output() create = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  mapName = '';
  aiMode: AiGenerationMode | null = null;
  mapIdPreset = false;
  creditsBalance = 0;
  creditsRequired = 1;
  isLoading = false;
  showErrorMsg = false;
  validationErrorText = '';

  // Creation progress tracking
  isCreating = false;
  isBackgroundTask = false;
  creationPhase: 'upload' | 'world' | 'complete' | 'failed' = 'upload';
  creationStageLabel = '';
  creationProgress = 0;
  creationErrorMsg = '';

  // Rotating hints during creation
  currentHintMessage = '';
  hintVisible = true;
  private readonly creationHints: string[] = [
    'Initializing reality engine and geography...',
    'Uploading map structure and Azgaar data...',
    'Analyzing terrain and biomes...',
    'Extracting culture and civilization data...',
    'Mapping political provinces and states...',
    'Generating deep historical lore...',
    'Populating world entities and relationships...',
    'Publishing knowledge base and wiki...',
    'Almost there — crafting your world...'
  ];
  private hintIndex = 0;
  private hintInterval: any = null;
  private pollingInterval: any = null;

  private userId: any = null;
  private userSub: Subscription | null = null;

  /** True when user chose Manual World Building — no credits needed */
  get isManualMode(): boolean {
    return this.aiMode === AiGenerationMode.Manual;
  }

  constructor(private stateService: WizardStateService,
    private currentUserService: CurrentUserService,
    private tokenService: AiPortraitTokenService,
    private mapCreationService: MapCreationService,
    private toastService: ToastService, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.stateService.state.pipe(take(1)).subscribe(state => {
      this.mapName = state.mapName || 'your world';
      this.aiMode = state.aiMode as AiGenerationMode || null;
      this.mapIdPreset = !!state.mapIdPreset;
      this.cdr.detectChanges();
    });

    this.userSub = this.currentUserService.currentUser.subscribe(user => {
      if (user) {
        this.userId = user.id || user.user_id;
        // Only fetch credits when AI features will be used
        if (!this.isManualMode) {
          this.fetchCreditBalance();
        }
        this.cdr.detectChanges();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.userSub) {
      this.userSub.unsubscribe();
    }
    this.clearPolling();
    this.stopHintCycling();
  }

  fetchCreditBalance(): void {
    if (!this.userId) return;
    this.isLoading = true;
    this.tokenService.getTokenBalance(this.userId).subscribe({
      next: (res: any) => {
        if (res && res.breakdown) {
          this.creditsBalance = res.breakdown.world_credits || 0;
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching credit balance:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onCreate(): void {
    this.showErrorMsg = false;
    this.validationErrorText = '';

    // Manual mode: no credits required — proceed directly
    if (!this.isManualMode && this.creditsBalance < this.creditsRequired) {
      this.validationErrorText = `Insufficient credits. You need ${this.creditsRequired} World Initialization Credit, but your balance is ${this.creditsBalance}.`;
      this.showErrorMsg = true;
      return;
    }

    const state = this.stateService.currentState;

    // Flow B: Existing Map (from maplist, mapId already preset)
    if (this.mapIdPreset) {
      this.isLoading = true;
      this.mapCreationService.createWorld(state, this.userId).subscribe({
        next: (res) => {
          this.isLoading = false;
          if (res && res.message) {
            const color = res.status === 'already_processing' ? 'warning' : 'success';
            this.toastService.showSuccess(res.message, color);
          } else {
            this.toastService.showSuccess('Your world is being built.', 'success');
          }
          this.create.emit();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error creating world:', err);
          this.validationErrorText = err?.error?.error || err?.error?.detail || 'Failed to initialize the world. Please try again.';
          this.showErrorMsg = true;
          this.isLoading = false;
        
          this.cdr.detectChanges();}
      });
      return;
    }

    // Flow A: Fresh Map creation via create-full/
    this.isCreating = true;
    this.creationPhase = 'upload';
    this.creationStageLabel = 'Uploading your map';
    this.creationProgress = 2;
    this.creationErrorMsg = '';
    this.startHintCycling();

    this.mapCreationService.createFull(state, this.userId).subscribe({
      next: (res: any) => {
        const mapId = String(res.map_id || '');
        if (mapId) {
          this.stateService.updateState({ mapId, isUploadInProgress: true });
          this.pollCreateFullStatus(mapId);
          this.cdr.detectChanges();
        } else {
          this.toastService.showSuccess('Your world is being built.', 'success');
          this.create.emit();
        }
      },
      error: (err) => {
        console.error('Error calling create-full:', err);
        this.isCreating = false;
        this.stopHintCycling();
        this.stateService.updateState({ isUploadInProgress: false });
        this.validationErrorText = err?.error?.error || err?.error?.detail || 'Failed to initialize the world. Please try again.';
        this.showErrorMsg = true;
      
          this.cdr.detectChanges();}
    });
  }

  private pollCreateFullStatus(mapId: string): void {
    this.clearPolling();
    this.pollingInterval = setInterval(() => {
      this.mapCreationService.getCreateFullStatus(mapId).subscribe({
        next: (res: any) => {
          // One bar for the whole wizard, from the backend (build_status): the
          // upload fills 0-60%, the world setup 60-100%, and it never goes back.
          const overall = res.overall;
          const setBar = (pct: number | undefined, fallback: number) =>
            (this.creationProgress = Math.max(this.creationProgress, pct ?? fallback));

          // Phase 1: Upload is in progress
          if (res.upload && res.wizard_status === 'pending') {
            this.creationPhase = 'upload';
            this.creationStageLabel = overall?.label || this.getUploadStageLabel(res.upload.stage);
            setBar(overall?.wizard_percent, res.upload.progress || 10);
            this.stateService.updateState({ isUploadInProgress: true });
            this.cdr.detectChanges();

            if (res.upload.stage === 'failed') {
              this.clearPolling();
              this.stopHintCycling();
              this.creationPhase = "failed";
              this.creationErrorMsg = res.error_message || res.upload.error || "Map upload and processing failed.";
              this.cdr.detectChanges();
              this.stateService.updateState({ isUploadInProgress: false });
            }
          }
          // Phase 2: World Creation is in progress or completed
          else if (res.wizard_status) {
            if (res.wizard_status === 'complete') {
              this.clearPolling();
              this.stopHintCycling();
              this.creationPhase = 'complete';
              this.creationProgress = 100;
              this.isBackgroundTask = false;
              this.stateService.updateState({
                creationStatus: 'complete',
                isBackgroundProcessing: false,
                isUploadInProgress: false
              });
              this.toastService.showSuccess('Your world is ready.', 'success');
              this.create.emit();
            } else if (res.wizard_status === 'failed') {
              this.clearPolling();
              this.stopHintCycling();
              this.creationPhase = 'failed';
              this.creationErrorMsg = res.error_message || 'World creation lore generation failed.';
              this.isBackgroundTask = false;
              this.stateService.updateState({
                creationStatus: 'failed',
                isBackgroundProcessing: false,
                isUploadInProgress: false
              });
              this.cdr.detectChanges();
            } else {
              this.creationPhase = 'world';
              this.creationStageLabel = overall?.label || this.getWorldStageLabel(res.wizard_status);
              // the setup continues the same bar (60-100%)
              setBar(overall?.wizard_percent, Math.max(res.pipeline?.progress || 0, 5));

              // When processing_ai or later background tasks start
              const isBg = res.wizard_status === 'processing_ai' || res.wizard_status === 'processing_wiki';
              this.isBackgroundTask = isBg;
              this.stateService.updateState({
                creationStatus: res.wizard_status,
                isBackgroundProcessing: isBg,
                isUploadInProgress: false
              });

              if (isBg) {
                // The AI part runs for a long time on the server and doesn't
                // need this window: close it, as building a world on an
                // already uploaded map does.
                this.clearPolling();
                this.stopHintCycling();
                this.toastService.showSuccess('Your world is being built.', 'success');
                this.create.emit();
              }
              this.cdr.detectChanges();
            }
          }
        },
        error: (err) => {
          // If status endpoint temporary 404 or network hiccup, don't immediately fail
          if (err.status !== 404) {
            console.warn('Status poll error:', err);
          }
        
          this.cdr.detectChanges();}
      });
    }, 4000);
  }

  private getUploadStageLabel(stage: string): string {
    switch (stage) {
      case 'uploading_files':
      case 'uploading':
        return 'Uploading Map & Files...';
      case 'processing_map':
        return 'Processing Map Geometry & Grid...';
      case 'saving_entities':
        return 'Extracting Cultures, States & Religions...';
      case 'pushing_wiki_stubs':
        return 'Creating Knowledge Base & Wiki Stubs...';
      case 'processing_cities':
        return 'Processing Cities & Biomes...';
      case 'complete':
        return 'Map Upload Complete! Starting Lore Generation...';
      case 'failed':
        return 'Map Processing Failed';
      default:
        return stage ? `Processing: ${stage}` : 'Processing Map...';
    }
  }

  private getWorldStageLabel(status: string): string {
    switch (status) {
      case 'pending':
        return 'Initializing World Settings...';
      case 'processing_map':
        return 'Recomputing Regional & Cell Resources...';
      case 'processing_lore':
        return 'Compiling World Lore & Timelines...';
      case 'processing_ai':
        return 'Generating Lore & History with AI...';
      case 'processing_wiki':
        return 'Updating Wiki & Entity Records...';
      case 'complete':
        return 'World Creation Complete!';
      case 'failed':
        return 'World Creation Failed';
      default:
        return status ? `Processing: ${status}` : 'Generating World...';
    }
  }

  private startHintCycling(): void {
    this.hintIndex = 0;
    this.currentHintMessage = this.creationHints[0];
    this.hintVisible = true;
    this.hintInterval = setInterval(() => {
      this.hintVisible = false;
      setTimeout(() => {
        this.hintIndex = (this.hintIndex + 1) % this.creationHints.length;
        this.currentHintMessage = this.creationHints[this.hintIndex];
        this.hintVisible = true;
      }, 400);
    }, 3500);
  }

  private stopHintCycling(): void {
    if (this.hintInterval) {
      clearInterval(this.hintInterval);
      this.hintInterval = null;
    }
  }

  private clearPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  onRetry(): void {
    this.isCreating = false;
    this.isBackgroundTask = false;
    this.creationPhase = 'upload';
    this.creationErrorMsg = '';
    this.showErrorMsg = false;
    this.stateService.updateState({
      creationStatus: '',
      isBackgroundProcessing: false,
      isUploadInProgress: false
    });
  }

  onBack(): void {
    this.back.emit();
  }
}
